import { OPEN_STATUSES, type RequestDetail } from "@procuremate/shared";
import { clsx } from "clsx";
import { Archive, ArrowLeft, CircleX, MessageCircleQuestion, RotateCcw } from "lucide-react";
import { type CSSProperties, type KeyboardEvent, type PointerEvent, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { Button } from "@/components/ui/button";
import { useCurrentUser } from "@/features/auth/api";
import { ApiRequestError } from "@/lib/api-client";
import { useFieldCatalogue, useReopenRequest, useRequestDetail } from "./api";
import { AttachmentTray, useAttachments } from "./attachments";
import { Conversation } from "./conversation";
import { type PanelActions, PanelActionsProvider } from "./panel-actions";
import { RequestPanel } from "./request-panel";
import { useConversation } from "./use-conversation";

type View = "conversation" | "details";

/** The details panel's width on wide screens, in rem (WP-2.8): resizable, remembered in this browser. */
const PANEL_REM = { min: 20, max: 48, default: 26, step: 2 } as const;
const WIDTH_KEY = "procuremate.panelWidth";

function storedWidth(): number {
  try {
    const value = Number(window.localStorage.getItem(WIDTH_KEY));
    return value >= PANEL_REM.min && value <= PANEL_REM.max ? value : PANEL_REM.default;
  } catch {
    return PANEL_REM.default;
  }
}

function clampWidth(rem: number): number {
  return Math.round(Math.min(PANEL_REM.max, Math.max(PANEL_REM.min, rem)));
}

/**
 * A request: the conversation and the live panel side by side on wide screens — the panel resizable
 * and expandable for a final review (WP-2.8); on phones they are two tabs (WP-2.3, ADR-0014).
 */
export function RequestPage() {
  const { id = "" } = useParams();
  const { t } = useTranslation();
  const me = useCurrentUser();
  const conversation = useConversation(id);
  const detail = useRequestDetail(id, { refetchInterval: conversation.refetchInterval });
  const catalogue = useFieldCatalogue();
  const [view, setView] = useState<View>("conversation");
  const tabsId = useId();
  const attachments = useAttachments(id, detail.data?.documents.length ?? 0);
  const [expanded, setExpanded] = useState(false);
  const [width, setWidth] = useState(storedWidth);
  const grid = useRef<HTMLDivElement>(null);
  const [focusMessage, setFocusMessage] = useState<{ id: string; n: number } | null>(null);
  const [draft, setDraft] = useState<{ text: string; n: number } | null>(null);
  useEffect(() => {
    try {
      window.localStorage.setItem(WIDTH_KEY, String(width));
    } catch {
      // Storage unavailable (private window, blocked): the width simply is not remembered.
    }
  }, [width]);
  // Reopening (ADR-0024) removes the button that had focus: continue in the composer. In an effect,
  // after React has rendered it (never requestAnimationFrame: it can run before the commit).
  const status = detail.data?.status;
  const previousStatus = useRef(status);
  useEffect(() => {
    if (previousStatus.current === "ABANDONED" && status === "DRAFT") document.getElementById("composer")?.focus();
    previousStatus.current = status;
  }, [status]);

  if (detail.isPending) return <p className="text-fg-muted">{t("app.loading")}</p>;
  if (detail.isError) {
    const notFound = detail.error instanceof ApiRequestError && detail.error.status === 404;
    return (
      <section className="rounded-lg bg-surface p-6 shadow-card">
        <p role="alert" className="text-fg">
          {notFound ? t("requests.page.notFound") : t("requests.page.loadError")}
        </p>
        <BackLink />
      </section>
    );
  }

  const request = detail.data;
  const isRequester = me.data?.id === request.requesterId;
  const open = OPEN_STATUSES.has(request.status);
  // Before classification the mandatory set is provisional: no count in the tab.
  const toGo = request.engagementShape ? request.gate.openMandatory.length : 0;
  const views: { key: View; label: string }[] = [
    { key: "conversation", label: t("requests.page.conversationTab") },
    {
      key: "details",
      label: toGo > 0 ? t("requests.page.detailsTabNeeded", { count: toGo }) : t("requests.page.detailsTab"),
    },
  ];

  // What the panel can ask of the page (WP-2.8): show where a value came from, start a message.
  const panelActions: PanelActions = {
    showMessage: (assistantId) => {
      // The value was recorded by that reply; the requester's words are the message before it.
      const at = request.messages.findIndex((m) => m.id === assistantId);
      const said = request.messages
        .slice(0, Math.max(at, 0))
        .reverse()
        .find((m) => m.role === "USER");
      setView("conversation");
      setExpanded(false);
      setFocusMessage((f) => ({ id: said?.id ?? assistantId, n: (f?.n ?? 0) + 1 }));
    },
    draftMessage: (text) => {
      setView("conversation");
      setExpanded(false);
      setDraft((d) => ({ text, n: (d?.n ?? 0) + 1 }));
    },
  };

  function onResizeKey(event: KeyboardEvent) {
    const by = { ArrowLeft: PANEL_REM.step, ArrowRight: -PANEL_REM.step } as Record<string, number>;
    if (event.key in by) setWidth((w) => clampWidth(w + (by[event.key] ?? 0)));
    else if (event.key === "Home") setWidth(PANEL_REM.min);
    else if (event.key === "End") setWidth(PANEL_REM.max);
    else return;
    event.preventDefault();
  }

  function onResizeDrag(event: PointerEvent<HTMLDivElement>) {
    if (event.buttons !== 1 || !grid.current) return;
    const rem = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    setWidth(clampWidth((grid.current.getBoundingClientRect().right - event.clientX) / rem));
  }

  function onTabKey(event: KeyboardEvent) {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const next: View = view === "conversation" ? "details" : "conversation";
    setView(next);
    document.getElementById(`${tabsId}-${next}-tab`)?.focus();
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <BackLink />
        <h1 className="mt-2 text-xl font-bold break-words text-primary sm:text-2xl">
          <span className="tabular">{request.reference}</span>
          {request.title ? <span className="font-semibold text-fg"> · {request.title}</span> : null}
        </h1>
      </div>

      {request.buyerNote ? <BuyerNote note={request.buyerNote} /> : null}
      {request.status === "ABANDONED" ? (
        <AbandonedNote requestId={request.id} abandonedAt={request.abandonedAt} canReopen={isRequester} />
      ) : null}

      {/* Phones and tablets: one view at a time. */}
      <div
        role="tablist"
        aria-label={t("requests.page.views")}
        className="flex gap-1 rounded-md bg-surface p-1 shadow-card lg:hidden"
      >
        {views.map((v) => (
          <button
            key={v.key}
            id={`${tabsId}-${v.key}-tab`}
            type="button"
            role="tab"
            aria-selected={view === v.key}
            aria-controls={`${tabsId}-${v.key}`}
            tabIndex={view === v.key ? 0 : -1}
            onClick={() => setView(v.key)}
            onKeyDown={onTabKey}
            className={clsx(
              "h-10 flex-1 rounded-sm px-3 text-sm font-semibold",
              view === v.key ? "bg-primary text-on-primary" : "text-primary hover:bg-bg",
            )}
          >
            {v.label}
          </button>
        ))}
      </div>

      <div
        ref={grid}
        style={{ "--panel-width": `${width}rem` } as CSSProperties}
        className={clsx(
          "grid items-start gap-6 lg:gap-2",
          expanded ? "lg:grid-cols-1" : "lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,var(--panel-width))]",
        )}
      >
        <div
          id={`${tabsId}-conversation`}
          role="tabpanel"
          aria-labelledby={`${tabsId}-conversation-tab`}
          className={clsx("min-w-0", view !== "conversation" && "hidden lg:block", expanded && "lg:hidden")}
        >
          <Conversation
            messages={request.messages}
            pending={conversation.pending}
            failure={conversation.failure}
            announcement={conversation.announcement}
            composer={!isRequester ? "hidden" : open ? "open" : "closed"}
            onSend={conversation.send}
            attachments={{
              onFiles: (files) => void attachments.add(files),
              tray: (
                <AttachmentTray
                  uploads={attachments.uploads}
                  documents={request.documents}
                  onDismiss={attachments.dismiss}
                />
              ),
            }}
            focusMessage={focusMessage}
            draft={draft}
          />
        </div>
        {expanded ? null : (
          // biome-ignore lint/a11y/useSemanticElements: a focusable, resizable separator (ARIA window splitter); <hr> cannot take focus or a value.
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label={t("requests.panel.bar.resize")}
            aria-valuemin={PANEL_REM.min}
            aria-valuemax={PANEL_REM.max}
            aria-valuenow={width}
            tabIndex={0}
            onKeyDown={onResizeKey}
            onPointerDown={(event) => event.currentTarget.setPointerCapture(event.pointerId)}
            onPointerMove={onResizeDrag}
            className="group hidden cursor-col-resize touch-none items-center justify-center self-stretch rounded-sm px-1 outline-none focus-visible:ring-2 focus-visible:ring-focus lg:sticky lg:top-4 lg:flex lg:h-[calc(100dvh-2rem)]"
          >
            <span className="h-14 w-1 rounded-full bg-border group-hover:bg-primary group-focus-visible:bg-primary" />
          </div>
        )}
        <div
          id={`${tabsId}-details`}
          role="tabpanel"
          // Focusable: on wide screens it scrolls on its own and may hold no other focusable element
          // (a submitted request): keyboard users must still reach it (axe scrollable-region-focusable;
          // a focusable tabpanel is the ARIA tabs pattern).
          // biome-ignore lint/a11y/noNoninteractiveTabindex: see above.
          tabIndex={0}
          aria-labelledby={`${tabsId}-details-tab`}
          className={clsx(
            "min-w-0",
            expanded
              ? "lg:mx-auto lg:w-full lg:max-w-5xl"
              : "lg:sticky lg:top-4 lg:flex lg:max-h-[calc(100dvh-2rem)] lg:flex-col",
            view !== "details" && "hidden lg:block",
          )}
        >
          <PanelActionsProvider value={isRequester ? panelActions : null}>
            <RequestPanel
              panel={request}
              catalogue={catalogue.data}
              canEdit={isRequester && open}
              editBlocked={conversation.pending !== null}
              suggestSubmit={conversation.suggestSubmit}
              expanded={expanded}
              onToggleExpand={() => setExpanded((e) => !e)}
            />
          </PanelActionsProvider>
        </div>
      </div>
    </div>
  );
}

/**
 * What procurement told the requester (WP-3.1): the buyer's questions while the request is returned
 * (answer them in the conversation, then submit again), or why it was not approved.
 */
function BuyerNote({ note }: { note: NonNullable<RequestDetail["buyerNote"]> }) {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const questions = note.kind === "questions";
  const when = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(new Date(note.createdAt));
  return (
    <section
      aria-labelledby={titleId}
      className={clsx(
        "rounded-lg border-l-4 bg-surface p-4 shadow-card",
        questions ? "border-l-warning-fg" : "border-l-danger",
      )}
    >
      <h2 id={titleId} className="flex items-center gap-2 font-heading text-base font-semibold text-fg">
        {questions ? (
          <MessageCircleQuestion aria-hidden="true" className="h-5 w-5 text-warning-fg" />
        ) : (
          <CircleX aria-hidden="true" className="h-5 w-5 text-danger" />
        )}
        {questions ? t("requests.buyerNote.questionsTitle") : t("requests.buyerNote.rejectionTitle")}
      </h2>
      <p className="mt-2 text-sm whitespace-pre-wrap break-words text-fg">{note.text}</p>
      <p className="mt-2 text-xs text-fg-muted">
        {questions
          ? t("requests.buyerNote.questionsHint", { date: when })
          : t("requests.buyerNote.rejectionHint", { date: when })}
      </p>
    </section>
  );
}

/**
 * A draft closed for inactivity (WP-3.5, ADR-0024): why, and Reopen for its requester. Once reopened
 * the conversation opens again and focus moves to the composer (RequestPage).
 */
function AbandonedNote({
  requestId,
  abandonedAt,
  canReopen,
}: {
  requestId: string;
  abandonedAt: string | null;
  canReopen: boolean;
}) {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const reopen = useReopenRequest(requestId);
  const when = abandonedAt
    ? new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(new Date(abandonedAt))
    : "—";
  return (
    <section aria-labelledby={titleId} className="rounded-lg border-l-4 border-l-border bg-surface p-4 shadow-card">
      <h2 id={titleId} className="flex items-center gap-2 font-heading text-base font-semibold text-fg">
        <Archive aria-hidden="true" className="h-5 w-5 text-fg-muted" />
        {t("requests.abandoned.title")}
      </h2>
      <p className="mt-2 max-w-prose text-sm text-fg">{t("requests.abandoned.body", { date: when })}</p>
      {canReopen ? (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button disabled={reopen.isPending} onClick={() => reopen.mutate()}>
            <RotateCcw aria-hidden="true" className="h-4 w-4" />
            {reopen.isPending ? t("requests.abandoned.reopening") : t("requests.abandoned.reopen")}
          </Button>
          {reopen.isError ? (
            <p role="alert" className="text-sm text-danger">
              {t("requests.abandoned.error")}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function BackLink() {
  const { t } = useTranslation();
  return (
    <Link to="/" className="inline-flex items-center gap-1 text-sm font-semibold text-link hover:underline">
      <ArrowLeft aria-hidden="true" className="h-4 w-4" />
      {t("requests.page.allRequests")}
    </Link>
  );
}
